// Display · EmptyState
//
// Quiet placeholder for "no results" / "no items yet" states inside lists,
// tables, peeks, and modals. Icon + title + optional description + optional
// primary CTA. Three size scales: sm (inline placeholders), md (list
// containers), lg (full-page empties).
//
// Atom-ish — one optional output event (onAction). Per the @ds convention,
// the empty `Actions` interface is dropped.
//
// All visual values come from `@ds/tokens/palette`.

import type { ReactNode } from "react";
import { Button } from "@ds/primitives/Button";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { TextValue, type TextSize } from "@ds/display/TextValue";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export type EmptyStateSize = "sm" | "md" | "lg";

export interface EmptyStateInput {
  /** Optional icon rendered large above the title. */
  icon?: ReactNode;
  /** Title line — short. */
  title: ReactNode;
  /** Optional description below the title. */
  description?: ReactNode;
  /** Optional CTA label — when set, renders a primary Button. */
  actionLabel?: ReactNode;
  /** Visual size scale.
   *  - `sm` — inline placeholders (compact icon + tight padding)
   *  - `md` — list / table container (default)
   *  - `lg` — full-page empties (large icon + generous padding)
   */
  size?: EmptyStateSize;
  /** `data-testid` applied to the root wrapper. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface EmptyStateOutput {
  /** Fired when the actionLabel Button is clicked. */
  onAction?: () => void;
}

// ─── Component type ──────────────────────────────────────────────────────

export type EmptyStateProps = EmptyStateInput & EmptyStateOutput;

// ─── Per-size visual constants ────────────────────────────────────────────

interface SizeConfig {
  /** Outer Box size token (padding scale). */
  outerBoxSize: "lg" | "xl";
  /** Stack gap token between icon / title / description / action. */
  stackGap: "sm" | "md";
  /** Title font size. */
  titleSize: TextSize;
  /** Description font size. */
  descSize: TextSize;
}

const SIZE: Record<EmptyStateSize, SizeConfig> = {
  sm: {
    outerBoxSize: "lg",   // SPACE.lg (16) — sm compact padding
    stackGap:     "sm",   // SPACE.sm (8)
    titleSize:    "md",   // 14 px
    descSize:     "sm",   // 13 px
  },
  md: {
    outerBoxSize: "xl",   // SPACE.xl (24) — md default padding
    stackGap:     "md",   // SPACE.md (12)
    titleSize:    "md",   // 14 px
    descSize:     "sm",   // 13 px
  },
  lg: {
    outerBoxSize: "xl",   // SPACE.xl (24) — lg full-page padding
    stackGap:     "md",   // SPACE.md (12)
    titleSize:    "lg",   // 16 px
    descSize:     "md",   // 14 px
  },
};

// ─── Implementation ──────────────────────────────────────────────────────

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  size = "md",
  testId,
  onAction,
}: EmptyStateProps) {
  const cfg = SIZE[size];

  return (
    <Box testId={testId} size={cfg.outerBoxSize} fullWidth>
      <Stack gap={cfg.stackGap} align="center">
        {icon && (
          <Box
            testId={testId ? `${testId}-icon` : undefined}
            tone="muted"
            border
            shape="pill"
            size="sm"
            shrink
          >
            <TextValue as="span" tone="faint">
              {icon}
            </TextValue>
          </Box>
        )}

        <TextValue
          testId={testId ? `${testId}-title` : undefined}
          size={cfg.titleSize}
          weight="medium"
          tone="primary"
          lineHeight="tight"
          align="center"
        >
          {title}
        </TextValue>

        {description && (
          <TextValue
            testId={testId ? `${testId}-description` : undefined}
            size={cfg.descSize}
            tone="muted"
            lineHeight="normal"
            align="center"
          >
            {description}
          </TextValue>
        )}

        {actionLabel && (
          <Box
            testId={testId ? `${testId}-action` : undefined}
            size="xs"
          >
            <Button
              label={actionLabel}
              variant="primary"
              size={size === "sm" ? "sm" : "md"}
              onClick={onAction}
              testId={testId ? `${testId}-action-btn` : undefined}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
